import { Loader2 } from "lucide-react";
import { useEffect, useMemo, useRef, useState, type ComponentType } from "react";

import { cn, pluralize } from "@/lib/utils";

import { sniffPreviewType } from "../files";
import { MarkdownText } from "../MarkdownText";
import { parseCsv } from "./csv";
import { declaredImageSize } from "./imageSize";
import {
  CSV_PREVIEW_CELL_CHARS,
  CSV_PREVIEW_COLUMNS,
  CSV_PREVIEW_ROWS,
  JSON_PREVIEW_CHARS,
  MAX_CANVAS_PIXELS,
  MAX_CANVAS_SIDE,
  MAX_IMAGE_PIXELS,
  PDF_PREVIEW_PAGES,
  type PreviewKind,
} from "./kinds";

export interface PreviewerProps {
  bytes: ArrayBuffer;
  name: string;
  /** The bytes are not what the name says (or do not decode): the download is offered instead. */
  onUnsupported: () => void;
  /** Too large to show (by what the file says of itself): the download is offered instead. */
  onTooLarge: () => void;
}

const count = (n: number) => n.toLocaleString("en-US");

/** The file as UTF-8 text (a byte order mark dropped, bad sequences replaced). */
const decodeText = (bytes: ArrayBuffer) => new TextDecoder("utf-8").decode(bytes);

function Loading() {
  return (
    <div role="status" className="flex flex-1 items-center justify-center p-8">
      <Loader2 className="h-5 w-5 text-muted-foreground motion-safe:animate-spin" aria-hidden />
      <span className="sr-only">Loading preview</span>
    </div>
  );
}

/** A note under a preview that shows only part of the file. */
function Shown({ children }: { children: string }) {
  return <p className="px-4 py-2 text-xs text-muted-foreground">{children}</p>;
}

/**
 * A raster image, by its own first bytes (PNG, JPEG, GIF, WebP), never its name: a blob of that
 * type only. An SVG or anything else named .png has no such bytes, and downloads. So does one whose
 * header says it is too many pixels to decode, or no pixels on a side, or does not say its size.
 */
function ImagePreview({ bytes, name, onUnsupported, onTooLarge }: PreviewerProps) {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    const head = new Uint8Array(bytes);
    const type = sniffPreviewType(head);
    if (!type?.startsWith("image/")) {
      onUnsupported();
      return;
    }
    const size = declaredImageSize(head);
    // A side of 0 makes any other side "few pixels": nothing true can be shown, so nothing is.
    if (!size || !(size.width > 0 && size.height > 0)) {
      onUnsupported();
      return;
    }
    if (size.width * size.height > MAX_IMAGE_PIXELS) {
      onTooLarge();
      return;
    }
    const next = URL.createObjectURL(new Blob([bytes], { type }));
    setUrl(next);
    return () => URL.revokeObjectURL(next);
  }, [bytes, onUnsupported, onTooLarge]);
  if (!url) return null;
  return (
    <div className="flex min-h-0 flex-1 items-center justify-center bg-muted p-4">
      <img
        src={url}
        alt={name}
        onError={onUnsupported}
        className="max-h-full max-w-full object-contain"
      />
    </div>
  );
}

/**
 * The first pages of a PDF (by its bytes), drawn on canvases by pdf.js, each in turn. A page is
 * drawn smaller when its canvas would pass `MAX_CANVAS_PIXELS`, or `MAX_CANVAS_SIDE` on a side.
 * How many of its pages it shows is said under it from the time it opens.
 */
function PdfPreview({ bytes, name, onUnsupported }: PreviewerProps) {
  const pagesBox = useRef<HTMLDivElement>(null);
  const [drawn, setDrawn] = useState(false);
  const [pages, setPages] = useState<{ total: number; shown: number } | null>(null);
  useEffect(() => {
    const box = pagesBox.current;
    if (!box) return;
    if (sniffPreviewType(new Uint8Array(bytes)) !== "application/pdf") {
      onUnsupported();
      return;
    }
    let stopped = false;
    let task: { destroy: () => Promise<void> } | null = null;
    void (async () => {
      try {
        const { openPdf } = await import("./pdfjs");
        if (stopped) return;
        // pdf.js hands its data to its worker: a copy, so the bytes stay usable here.
        const loading = openPdf(new Uint8Array(bytes.slice(0)));
        task = loading;
        const pdf = await loading.promise;
        const shown = Math.min(pdf.numPages, PDF_PREVIEW_PAGES);
        setPages({ total: pdf.numPages, shown });
        for (let number = 1; number <= shown && !stopped; number++) {
          const page = await pdf.getPage(number);
          const unit = page.getViewport({ scale: 1 });
          const scale = Math.min(
            1.5,
            Math.sqrt(MAX_CANVAS_PIXELS / (unit.width * unit.height)),
            MAX_CANVAS_SIDE / Math.max(unit.width, unit.height)
          );
          const viewport = page.getViewport({ scale });
          const canvas = document.createElement("canvas");
          canvas.width = Math.max(1, Math.floor(viewport.width));
          canvas.height = Math.max(1, Math.floor(viewport.height));
          canvas.setAttribute("role", "img");
          canvas.setAttribute("aria-label", `${name}, page ${number}`);
          canvas.className = "h-auto w-full max-w-3xl border bg-white shadow-sm";
          const canvasContext = canvas.getContext("2d");
          if (!canvasContext) throw new Error("no canvas");
          await page.render({ canvas, canvasContext, viewport }).promise;
          if (stopped) break;
          box.append(canvas);
          setDrawn(true);
        }
      } catch {
        if (!stopped) onUnsupported();
      }
    })();
    return () => {
      stopped = true;
      void task?.destroy();
      box.replaceChildren();
    };
  }, [bytes, name, onUnsupported]);
  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="flex min-h-0 flex-1 flex-col overflow-y-auto bg-muted">
        {!drawn && <Loading />}
        <div ref={pagesBox} className="flex flex-col items-center gap-3 p-4" />
      </div>
      {pages && pages.total > pages.shown && (
        <Shown>{`First ${pages.shown} of ${count(pages.total)} pages`}</Shown>
      )}
    </div>
  );
}

/** A number as a statement writes one: "1,234.50", "-$12", "(40.00)", "3%". */
const NUMBER = /^[-+]?\(?[-+]?[$€£]?\d[\d,]*(\.\d+)?\)?%?$|^[-+]?\(?[$€£]?\.\d+\)?%?$/;

/** The columns whose cells under the header are all numbers (and one at least): right-aligned. */
function numericColumns(body: string[][]) {
  const columns = new Set<number>();
  const width = Math.max(0, ...body.map((row) => row.length));
  for (let index = 0; index < width; index++) {
    const cells = body.map((row) => (row[index] ?? "").trim()).filter(Boolean);
    if (cells.length > 0 && cells.every((cell) => NUMBER.test(cell))) columns.add(index);
  }
  return columns;
}

/**
 * A CSV's header and first rows and columns, as text cells (nothing in it is markup), in one
 * scroller with the header row and first column kept in view. Text wraps at about 40 characters
 * (its row grows); numbers stay on one line, right-aligned.
 */
function CsvPreview({ bytes }: PreviewerProps) {
  const table = useMemo(
    () =>
      parseCsv(decodeText(bytes), CSV_PREVIEW_ROWS, CSV_PREVIEW_COLUMNS, CSV_PREVIEW_CELL_CHARS),
    [bytes]
  );
  const [header, ...body] = table.rows;
  const numeric = useMemo(() => numericColumns(table.rows.slice(1)), [table]);
  // Sticky cells carry the dialog's own background, so nothing scrolled under them shows: the
  // header over the body and the first column, the corner over both. The first column's edge is
  // ruled, so text scrolled under it is cut at a line, not in mid-air.
  const cellClass = (index: number) =>
    cn(
      "border-b bg-background px-2 align-top",
      index === 0 && "sticky left-0 z-[1] border-r",
      numeric.has(index) && "whitespace-nowrap text-right tabular-nums"
    );
  // Long text stops at three lines; the whole of it is in the download.
  const text = (cell: string, index: number) =>
    numeric.has(index) ? (
      cell
    ) : (
      <div className="line-clamp-3 max-w-[40ch] whitespace-pre-wrap break-words">{cell}</div>
    );
  // Rows under the header, which is not one of them.
  const totalRows = Math.max(0, table.totalRows - 1);
  const notes = [
    totalRows > body.length
      ? `First ${count(body.length)} of ${count(totalRows)} rows`
      : pluralize(totalRows, "row"),
    table.totalColumns > CSV_PREVIEW_COLUMNS &&
      `first ${CSV_PREVIEW_COLUMNS} of ${count(table.totalColumns)} columns`,
  ].filter(Boolean);
  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="min-h-0 flex-1 overflow-auto">
        {header && (
          // Separate borders, as collapsed ones stay behind when their sticky cell moves.
          <table className="w-max min-w-full border-separate border-spacing-0 text-left text-xs">
            <thead>
              <tr>
                {header.map((cell, index) => (
                  <th
                    key={index}
                    className={cn(
                      cellClass(index),
                      "sticky top-0 py-1.5 font-medium",
                      index === 0 ? "z-[3]" : "z-[2]"
                    )}
                  >
                    {text(cell, index)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {body.map((row, rowIndex) => (
                <tr key={rowIndex}>
                  {row.map((cell, index) => (
                    <td key={index} className={cn(cellClass(index), "py-1")}>
                      {text(cell, index)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      <Shown>{notes.join(", ")}</Shown>
    </div>
  );
}

/** Markdown through the chat's renderer: no raw HTML, no images, only safe links. */
function MarkdownPreview({ bytes }: PreviewerProps) {
  const text = useMemo(() => decodeText(bytes), [bytes]);
  return (
    <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
      <MarkdownText text={text} />
    </div>
  );
}

/** JSON pretty-printed (as it is when it does not parse), cut at a length. */
function JsonPreview({ bytes }: PreviewerProps) {
  const text = useMemo(() => {
    const raw = decodeText(bytes);
    try {
      return JSON.stringify(JSON.parse(raw), null, 2);
    } catch {
      return raw;
    }
  }, [bytes]);
  const cut = text.length > JSON_PREVIEW_CHARS;
  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <pre className="min-h-0 flex-1 overflow-auto whitespace-pre p-4 text-xs">
        {cut ? text.slice(0, JSON_PREVIEW_CHARS) : text}
      </pre>
      {cut && <Shown>{`First ${count(JSON_PREVIEW_CHARS)} characters`}</Shown>}
    </div>
  );
}

/**
 * The previewer for each kind: the one place a new preview plugs in. #896's chart card takes
 * chart JSON (a ChartSpec or Vega-Lite file) from here; until then JSON shows as text.
 */
export const PREVIEWERS: Record<PreviewKind, ComponentType<PreviewerProps>> = {
  image: ImagePreview,
  pdf: PdfPreview,
  csv: CsvPreview,
  markdown: MarkdownPreview,
  json: JsonPreview,
};
